import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Loader2, AlertCircle } from 'lucide-react';
import { trackLead } from '../../lib/tracking';

interface Props {
  sourceTag: string;
  title: string;
  description: string;
}

type Phase = 'form' | 'submitting' | 'error';

export default function LeadCaptureForm({ sourceTag, title, description }: Props) {
  const [phase, setPhase] = useState<Phase>('form');
  const [fields, setFields] = useState({ firstName: '', lastName: '', phone: '' });
  const navigate = useNavigate();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setPhase('submitting');
    try {
      const body = new URLSearchParams({
        'form-name': 'lead-capture',
        source: sourceTag,
        firstName: fields.firstName.trim(),
        lastName: fields.lastName.trim(),
        phone: fields.phone.trim(),
      });
      const res = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (!res.ok) throw new Error('submit failed');
      trackLead(sourceTag);
      navigate(`/lp/${sourceTag}/thank-you`, { state: { firstName: fields.firstName.trim() } });
    } catch {
      setPhase('error');
    }
  }

  function updateField(key: keyof typeof fields) {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      setFields((prev) => ({ ...prev, [key]: e.target.value }));
    };
  }

  return (
    <div className="w-full max-w-xl mx-auto">
      <motion.form
        onSubmit={handleSubmit}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full"
        noValidate
      >
        <div className="text-center mb-8">
          <h3 className="text-2xl md:text-3xl lg:text-4xl font-normal leading-tight tracking-tight text-text-primary mb-3 text-balance">
            {title}
          </h3>
          <p className="text-base text-muted text-balance">{description}</p>
        </div>

        <div className="space-y-4 text-left">
          <div>
            <label htmlFor="firstName" className="block text-xs font-medium text-muted uppercase tracking-wider mb-2">
              Imię
            </label>
            <input
              id="firstName"
              name="firstName"
              type="text"
              required
              autoComplete="given-name"
              value={fields.firstName}
              onChange={updateField('firstName')}
              className="w-full bg-bg border border-stroke rounded-xl px-4 py-3 text-base text-text-primary placeholder:text-muted focus:border-text-primary focus:outline-none transition-colors"
              placeholder="Jan"
            />
          </div>

          <div>
            <label htmlFor="lastName" className="block text-xs font-medium text-muted uppercase tracking-wider mb-2">
              Nazwisko
            </label>
            <input
              id="lastName"
              name="lastName"
              type="text"
              required
              autoComplete="family-name"
              value={fields.lastName}
              onChange={updateField('lastName')}
              className="w-full bg-bg border border-stroke rounded-xl px-4 py-3 text-base text-text-primary placeholder:text-muted focus:border-text-primary focus:outline-none transition-colors"
              placeholder="Kowalski"
            />
          </div>

          <div>
            <label htmlFor="phone" className="block text-xs font-medium text-muted uppercase tracking-wider mb-2">
              Numer telefonu
            </label>
            <input
              id="phone"
              name="phone"
              type="tel"
              required
              autoComplete="tel"
              pattern="[+\d\s\-()]{7,}"
              value={fields.phone}
              onChange={updateField('phone')}
              className="w-full bg-bg border border-stroke rounded-xl px-4 py-3 text-base text-text-primary placeholder:text-muted focus:border-text-primary focus:outline-none transition-colors"
              placeholder="+48 600 000 000"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={phase === 'submitting'}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-text-primary text-bg px-8 py-4 rounded-full text-base font-medium hover:scale-[1.01] transition-transform disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {phase === 'submitting' ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Wysyłam...
            </>
          ) : (
            <>
              Zamów callback
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>

        {phase === 'error' && (
          <div className="mt-4 flex items-center justify-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-2">
            <AlertCircle className="w-4 h-4" />
            Coś poszło nie tak. Spróbuj ponownie lub zadzwoń bezpośrednio.
          </div>
        )}

        <p className="text-xs text-muted mt-5 text-center leading-relaxed max-w-md mx-auto">
          Wysyłając akceptujesz{' '}
          <Link to="/polityka-prywatnosci" className="underline hover:text-text-primary">
            politykę prywatności
          </Link>
          {' '}i{' '}
          <Link to="/rodo" className="underline hover:text-text-primary">
            klauzulę RODO
          </Link>
          . Dane służą wyłącznie do kontaktu telefonicznego (speed-to-lead).
        </p>
      </motion.form>
    </div>
  );
}
